import React, { useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { AssetDetails as AssetDetailsComponent } from '@innovyze/lib-am-common/Components';
import {
	getAssetPipe,
	getInspectionAttributes,
	getAssetDetail,
} from '@innovyze/lib-am-common/Actions';
import { useDispatch } from 'react-redux';
import { getSubscriptions, useTzDateTime } from '@innovyze/stylovyze';
import { InspectionStandard } from '@innovyze/lib-am-common/types/inspection.types';
import { selectRiskSetting } from '@Selectors';
import { useGlobalization } from '@Translations/useGlobalization';
import { fullAccess } from '@innovyze/shared-utils';
import { getSystemTypeIdByAssetTypePrefix } from '@innovyze/lib_asset_schema';

// Decode a route param, tolerating a malformed %-sequence (which would otherwise
// throw a URIError from decodeURIComponent and crash the page).
const safeDecodeURIComponent = (value: string): string => {
	try {
		return decodeURIComponent(value);
	} catch {
		return value;
	}
};

const AssetDetails = () => {
	const { t } = useGlobalization();
	const params: { assetId: string } = useParams();
	// The assetId arrives percent-encoded in the route (e.g. a '/' as %2F);
	// decode it once here so the lib-am-common actions receive the raw id and
	// encode it themselves for the API call (avoids double-encoding).
	const assetId = safeDecodeURIComponent(params.assetId);
	const history = useHistory();
	const dispatch = useDispatch();
	const formatDateUTC = useTzDateTime({ timeZone: 'UTC' }).formatDate;

	const riskSetting = selectRiskSetting();
	const assetType = riskSetting.assetType;

	const subscriptions = getSubscriptions();
	const hasFullAccess = fullAccess(subscriptions);

	const [systemType, setSystemType] = useState<string | undefined>(undefined);

	useEffect(() => {
		dispatch(
			getAssetDetail({
				assetId: assetId,
				assetType: assetType,
				formatDateUTC: formatDateUTC,
				systemType: systemType,
			}),
		);
		dispatch(
			getAssetPipe({
				assetId: assetId,
				assetType: assetType,
				formatDateUTC: formatDateUTC,
				systemType: systemType,
			}),
		);
		dispatch(
			getInspectionAttributes({
				assetId: assetId,
				assetType: assetType,
			}),
		);
	}, [assetType, systemType, dispatch]);

	useEffect(() => {
		const systemType = getSystemTypeIdByAssetTypePrefix(assetType);

		setSystemType(systemType);
	}, [assetType]);

	const back = () => {
		history.goBack();
	};

	const viewInspection = (
		videoURL: string,
		inspectionId: string,
		standard: InspectionStandard,
	) => {
		history.push(`../riskInspectionDetails/${standard}/${inspectionId}`);
	};

	const actions = {
		onInspectionDetailClicked: viewInspection,
	};

	return (
		<AssetDetailsComponent
			assetId={assetId}
			back={{
				action: back,
				label: t('Back to Results'),
			}}
			assetType={assetType}
			actions={actions}
			adminFullAccess={hasFullAccess}
			systemType={systemType}
		/>
	);
};

export default AssetDetails;
